import IdDocType from '@/resources/enum/IdDocType';

export const supportedLanguages = {
  ja: 'Japanese',
  vi: 'Vietnamese',
  en: 'English',
  id: 'Indonesian',
  ms: 'Malay',
  th: 'Thai',
  zh: 'Simplified Chinese'
} as const;

/**
 * 翻訳シートで定義されているドキュメント名一覧
 * @see https://docs.google.com/spreadsheets/d/1IhH7NnuIYwRLhl5q4oIhTWdSZAl_hJE64PWuAwA_ASk/edit#gid=1739646188&range=B:B
 */
export type I18nDocumentName =
  | 'drivers-license'
  | 'drivers-license-auto'
  | 'residence-card'
  | 'my-number-card'
  | 'my-number-card-auto'
  | 'personal-number-document'
  | 'passport'
  | 'driving-history-certificate'
  | 'driving-history-certificate-auto'
  | 'health-insurance-card'
  | 'physical-disability-certificate-book'
  | 'special-education-book'
  | 'mental-disability-certificate-book'
  | 'pension-book'
  | 'basic-pension-number-notification'
  | 'mental-disability-certificate-card'
  | 'special-education-card'
  | 'physical-disability-certificate-card'
  | 'foreign-passport'
  | 'student-id-card'
  | 'student-notebook'
  | 'employee-id-card'
  | 'basic-resident-registration-card'
  | 'special-permanent-resident-certificate'
  | 'mother-and-baby-notebook'
  | 'certificate-of-residence'
  | 'certificate-of-family-register'
  | 'certificate-of-individual-records'
  | 'global-drivers-license'
  | 'global-residence-card'
  | 'global-my-number-card'
  | 'global-passport'
  | 'philid'
  | 'tinid'
  | 'sssid'
  | 'umid2'
  | 'umid'
  | 'nric'
  | 'work-permit'
  | 'ktp'
  | 'kia-over5'
  | 'kia-under5'
  | 'kitap'
  | 'cccd'
  | 'cmnd'
  | 'mypr'
  | 'mykad'
  | 'aadhaar';

// i18nテーブルで使用されるドキュメント名
export const i18nDocumentNames: Record<IdDocType, I18nDocumentName | undefined> = {
  [IdDocType.DriversLicenseHo]: 'drivers-license',
  [IdDocType.DriversLicenseTo]: 'drivers-license',
  [IdDocType.DriversLicenseTi]: 'drivers-license',
  [IdDocType.MyNumberCardHo]: 'my-number-card',
  [IdDocType.MyNumberCardTo]: 'my-number-card',
  [IdDocType.MyNumberCardTi]: 'my-number-card',
  [IdDocType.ResidenceCardHo]: 'residence-card',
  [IdDocType.ResidenceCardTo]: 'residence-card',
  [IdDocType.ResidenceCardTi]: 'residence-card',
  [IdDocType.SpecialPermanentResidentCertificateHo]: 'special-permanent-resident-certificate',
  [IdDocType.SpecialPermanentResidentCertificateTo]: 'special-permanent-resident-certificate',
  [IdDocType.SpecialPermanentResidentCertificateTi]: 'special-permanent-resident-certificate',
  [IdDocType.PassportHo]: 'passport',
  [IdDocType.PassportTo]: 'passport',
  [IdDocType.PassportTi]: 'passport',
  [IdDocType.DrivingHistoryCertificateHo]: 'driving-history-certificate',
  [IdDocType.DrivingHistoryCertificateTo]: 'driving-history-certificate',
  [IdDocType.DrivingHistoryCertificateTi]: 'driving-history-certificate',
  [IdDocType.HealthInsuranceCardTo]: 'health-insurance-card',
  [IdDocType.HealthInsuranceCardTi]: 'health-insurance-card',
  [IdDocType.PhysicalDisabilityCertificateBookTo]: 'physical-disability-certificate-book',
  [IdDocType.PhysicalDisabilityCertificateCardTo]: 'physical-disability-certificate-card',
  [IdDocType.SpecialEducationBookTo]: 'special-education-book',
  [IdDocType.SpecialEducationCardTo]: 'special-education-card',
  [IdDocType.StudentNotebookTo]: 'student-notebook',
  [IdDocType.MentalDisabilityCertificateBookTo]: 'mental-disability-certificate-book',
  [IdDocType.MentalDisabilityCertificateCardTo]: 'mental-disability-certificate-card',
  [IdDocType.PensionBookTo]: 'pension-book',
  [IdDocType.BasicPensionNumberNotificationTo]: 'basic-pension-number-notification',
  [IdDocType.PassportAnyIssuanceDateTo]: 'passport',
  [IdDocType.BasicResidentRegistrationCardTo]: 'basic-resident-registration-card',
  [IdDocType.StudentIDCardTo]: 'student-id-card',
  [IdDocType.PhysicalDisabilityCertificateBookTi]: 'physical-disability-certificate-book',
  [IdDocType.PhysicalDisabilityCertificateCardTi]: 'physical-disability-certificate-card',
  [IdDocType.SpecialEducationBookTi]: 'special-education-book',
  [IdDocType.SpecialEducationCardTi]: 'special-education-card',
  [IdDocType.StudentNotebookTi]: 'student-notebook',
  [IdDocType.MentalDisabilityCertificateBookTi]: 'mental-disability-certificate-book',
  [IdDocType.MentalDisabilityCertificateCardTi]: 'mental-disability-certificate-card',
  [IdDocType.PensionBookTi]: 'pension-book',
  [IdDocType.BasicPensionNumberNotificationTi]: 'basic-pension-number-notification',
  [IdDocType.PassportAnyIssuanceDateTi]: 'passport',
  [IdDocType.StudentIDCardTi]: 'student-id-card',
  [IdDocType.BasicResidentRegistrationCardTi]: 'basic-resident-registration-card',
  [IdDocType.MyNumberCardAndBack]: 'my-number-card',
  [IdDocType.DriversLicenseGlobalNoFace]: 'global-drivers-license',
  [IdDocType.NationalIdentityCardGlobalNoFace]: 'global-my-number-card',
  [IdDocType.ResidenceCardGlobalNoFace]: 'global-residence-card',
  [IdDocType.PassportGlobalNoFace]: 'global-passport',
  [IdDocType.PhilIDGlobalNoFace]: 'philid',
  [IdDocType.TINIDGlobalNoFace]: 'tinid',
  [IdDocType.SSSIDGlobalNoFace]: 'sssid',
  [IdDocType.UMIDGlobalNoFace]: 'umid',
  [IdDocType.UMID2GlobalNoFace]: 'umid2',
  [IdDocType.NRICGlobalNoFace]: 'nric',
  [IdDocType.WorkPermitGlobalNoFace]: 'work-permit',
  [IdDocType.KTPGlobalNoFace]: 'ktp',
  [IdDocType.KIAOver5GlobalNoFace]: 'kia-over5',
  [IdDocType.KIAUnder5GlobalNoFace]: 'kia-under5',
  [IdDocType.KITAPGlobalNoFace]: 'kitap',
  [IdDocType.CCCDGlobalNoFace]: 'cccd',
  [IdDocType.CMNDGlobalNoFace]: 'cmnd',
  [IdDocType.MyPRGlobalNoFace]: 'mypr',
  [IdDocType.MyKADGlobalNoFace]: 'mykad',
  [IdDocType.AadhaarGlobalNoFace]: 'aadhaar',
  [IdDocType.DriversLicenseGlobalFace]: 'global-drivers-license',
  [IdDocType.NationalIdentityCardGlobalFace]: 'global-my-number-card',
  [IdDocType.ResidenceCardGlobalFace]: 'global-residence-card',
  [IdDocType.PassportGlobalFace]: 'global-passport',
  [IdDocType.PhilIDGlobalFace]: 'philid',
  [IdDocType.TINIDGlobalFace]: 'tinid',
  [IdDocType.SSSIDGlobalFace]: 'sssid',
  [IdDocType.UMIDGlobalFace]: 'umid',
  [IdDocType.UMID2GlobalFace]: 'umid2',
  [IdDocType.NRICGlobalFace]: 'nric',
  [IdDocType.WorkPermitGlobalFace]: 'work-permit',
  [IdDocType.KTPGlobalFace]: 'ktp',
  [IdDocType.KIAOver5GlobalFace]: 'kia-over5',
  [IdDocType.KITAPGlobalFace]: 'kitap',
  [IdDocType.CCCDGlobalFace]: 'cccd',
  [IdDocType.CMNDGlobalFace]: 'cmnd',
  [IdDocType.MyPRGlobalFace]: 'mypr',
  [IdDocType.MyKADGlobalFace]: 'mykad',
  [IdDocType.AadhaarGlobalFace]: 'aadhaar',
  [IdDocType.MnGeneral]: 'personal-number-document',
  [IdDocType.GpGeneral]: undefined,
  [IdDocType.Unknown]: undefined,
  /**
   * 表面撮影方式
   * 既存仕様タイプの文言は各書類既存のものと同様にする
   */
  [IdDocType.DriversLicenseFrontFace]: 'drivers-license',
  [IdDocType.MyNumberCardFrontFace]: 'my-number-card',
  [IdDocType.ResidenceCardFrontFace]: 'residence-card',
  [IdDocType.SpecialPermanentResidentCertificateFrontFace]: 'special-permanent-resident-certificate',
  [IdDocType.PassportFrontFace]: 'passport',
  [IdDocType.DrivingHistoryCertificateFrontFace]: 'driving-history-certificate',
  [IdDocType.PassportAnyIssuanceDateFrontFace]: 'passport',
  [IdDocType.ForeignPassportFrontFace]: 'foreign-passport',
  [IdDocType.StudentIDCardFrontFace]: 'student-id-card',
  [IdDocType.BasicResidentRegistrationCardFrontFace]: 'basic-resident-registration-card',
  [IdDocType.StudentNotebookFrontFace]: 'student-notebook',
  [IdDocType.DriversLicenseFront]: 'drivers-license',
  [IdDocType.MyNumberCardFront]: 'my-number-card',
  [IdDocType.ResidenceCardFront]: 'residence-card',
  [IdDocType.SpecialPermanentResidentCertificateFront]: 'special-permanent-resident-certificate',
  [IdDocType.PassportFront]: 'passport',
  [IdDocType.DrivingHistoryCertificateFront]: 'driving-history-certificate',
  [IdDocType.PassportAnyIssuanceDateFront]: 'passport',
  [IdDocType.ForeignPassportFront]: 'foreign-passport',
  [IdDocType.HealthInsuranceCardFront]: 'health-insurance-card',
  [IdDocType.PhysicalDisabilityCertificateCardFront]: 'physical-disability-certificate-card',
  [IdDocType.SpecialEducationCardFront]: 'special-education-card',
  [IdDocType.MentalDisabilityCertificateCardFront]: 'mental-disability-certificate-card',
  [IdDocType.StudentIDCardFront]: 'student-id-card',
  [IdDocType.BasicResidentRegistrationCardFront]: 'basic-resident-registration-card',
  [IdDocType.EmployeeIDCardFront]: 'employee-id-card',
  [IdDocType.PhysicalDisabilityCertificateBookFront]: 'physical-disability-certificate-book',
  [IdDocType.SpecialEducationBookFront]: 'special-education-book',
  [IdDocType.MentalDisabilityCertificateBookFront]: 'mental-disability-certificate-book',
  [IdDocType.PensionBookFront]: 'pension-book',
  [IdDocType.BasicPensionNumberNotificationFront]: 'basic-pension-number-notification',
  [IdDocType.StudentNotebookFront]: 'student-notebook',
  [IdDocType.MotherAndBabyNotebookFront]: 'mother-and-baby-notebook',
  [IdDocType.CertificateOfResidenceFront]: 'certificate-of-residence',
  [IdDocType.CertificateOfFamilyRegisterFront]: 'certificate-of-family-register',
  [IdDocType.CertificateOfIndividualRecordsFront]: 'certificate-of-individual-records',
  /**
   * 裏面撮影方式
   * 既存仕様タイプの文言は各書類既存のものと同様にする
   */
  [IdDocType.DriversLicenseFrontBackFace]: 'drivers-license',
  [IdDocType.MyNumberCardFrontBackFace]: 'my-number-card',
  [IdDocType.ResidenceCardFrontBackFace]: 'residence-card',
  [IdDocType.SpecialPermanentResidentCertificateFrontBackFace]: 'special-permanent-resident-certificate',
  [IdDocType.PassportFrontBackFace]: 'passport',
  [IdDocType.DrivingHistoryCertificateFrontBackFace]: 'driving-history-certificate',
  [IdDocType.PassportAnyIssuanceDateFrontBackFace]: 'passport',
  [IdDocType.ForeignPassportFrontBackFace]: 'foreign-passport',
  [IdDocType.StudentIDCardFrontBackFace]: 'student-id-card',
  [IdDocType.BasicResidentRegistrationCardFrontBackFace]: 'basic-resident-registration-card',
  [IdDocType.StudentNotebookFrontBackFace]: 'student-notebook',
  [IdDocType.DriversLicenseFrontBack]: 'drivers-license',
  [IdDocType.MyNumberCardFrontBack]: 'my-number-card',
  [IdDocType.ResidenceCardFrontBack]: 'residence-card',
  [IdDocType.SpecialPermanentResidentCertificateFrontBack]: 'special-permanent-resident-certificate',
  [IdDocType.PassportFrontBack]: 'passport',
  [IdDocType.DrivingHistoryCertificateFrontBack]: 'driving-history-certificate',
  [IdDocType.PassportAnyIssuanceDateFrontBack]: 'passport',
  [IdDocType.ForeignPassportFrontBack]: 'foreign-passport',
  [IdDocType.HealthInsuranceCardFrontBack]: 'health-insurance-card',
  [IdDocType.PhysicalDisabilityCertificateCardFrontBack]: 'physical-disability-certificate-card',
  [IdDocType.SpecialEducationCardFrontBack]: 'special-education-card',
  [IdDocType.MentalDisabilityCertificateCardFrontBack]: 'mental-disability-certificate-card',
  [IdDocType.StudentIDCardFrontBack]: 'student-id-card',
  [IdDocType.BasicResidentRegistrationCardFrontBack]: 'basic-resident-registration-card',
  [IdDocType.EmployeeIDCardFrontBack]: 'employee-id-card',
  [IdDocType.PhysicalDisabilityCertificateBookFrontBack]: 'physical-disability-certificate-book',
  [IdDocType.SpecialEducationBookFrontBack]: 'special-education-book',
  [IdDocType.MentalDisabilityCertificateBookFrontBack]: 'mental-disability-certificate-book',
  [IdDocType.PensionBookFrontBack]: 'pension-book',
  [IdDocType.BasicPensionNumberNotificationFrontBack]: 'basic-pension-number-notification',
  [IdDocType.StudentNotebookFrontBack]: 'student-notebook',
  [IdDocType.MotherAndBabyNotebookFrontBack]: 'mother-and-baby-notebook',
  [IdDocType.CertificateOfResidenceFrontBack]: 'certificate-of-residence',
  [IdDocType.CertificateOfFamilyRegisterFrontBack]: 'certificate-of-family-register',
  [IdDocType.CertificateOfIndividualRecordsFrontBack]: 'certificate-of-individual-records'
};

type ShootType = 'front' | 'diagonal' | 'back' | 'face-front' | 'face-action';
type DeviceType = 'pc' | 'sp';
export type MediaList = {
  i18nKeyIntroductionImage1?: string;
  i18nKeyIntroductionImage2?: string;
  i18nKeyIntroductionImage3?: string;
  i18nKeyIntroductionImage4?: string;
  i18nKeyIntroductionMovie?: {
    [key in keyof typeof supportedLanguages]: string;
  };
};

/**
 * 書類ごとのインストラクション画像・動画
 * PCは基本的にコンポーネント側で共通になっている
 */
export const instructionMediaByDocument: {
  [key in
    | I18nDocumentName
    | 'general-book'
    | 'general-card'
    | 'general-purpose-document'
    | 'optioal-document'
    | 'face'
    | 'mynumber-document-notification-card'
    | 'mynumber-document-residence-certificate'
    | 'mynumber-document-unknown']?: {
    [key in DeviceType]: {
      [key in ShootType]?: MediaList;
    };
  };
} = {
  'drivers-license': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_drivers_license_front_ja@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_drivers_license_front_ja@2x.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_manual_drivers_license_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_manual_drivers_license_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_manual_drivers_license_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_manual_drivers_license_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_manual_drivers_license_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_manual_drivers_license_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_manual_drivers_license_diagonal_zh-cn.mp4'
        }
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_drivers_license_back_ja@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_drivers_license_back_ja@2x.png'
      }
    }
  },
  'drivers-license-auto': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_autodetect_drivers_license_front_ja.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_autodetect_drivers_license_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_autodetect_drivers_license_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_autodetect_drivers_license_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_autodetect_drivers_license_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_autodetect_drivers_license_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_autodetect_drivers_license_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_autodetect_drivers_license_diagonal_zh-cn.mp4'
        }
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_autodetect_drivers_license_back_ja.png'
      }
    }
  },
  'driving-history-certificate': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_drivers_license_front_ja@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_drivers_license_front_ja@2x.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_manual_driving_history_certificate_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_manual_driving_history_certificate_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_manual_driving_history_certificate_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_manual_driving_history_certificate_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_manual_driving_history_certificate_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_manual_driving_history_certificate_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_manual_driving_history_certificate_diagonal_zh-cn.mp4'
        }
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_drivers_license_back_ja@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_drivers_license_back_ja@2x.png'
      }
    }
  },
  'driving-history-certificate-auto': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_autodetect_drivers_license_front_ja.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_autodetect_driving_history_certificate_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_autodetect_driving_history_certificate_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_autodetect_driving_history_certificate_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_autodetect_driving_history_certificate_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_autodetect_driving_history_certificate_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_autodetect_driving_history_certificate_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_autodetect_driving_history_certificate_diagonal_zh-cn.mp4'
        }
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_autodetect_drivers_license_back_ja.png'
      }
    }
  },
  'foreign-passport': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/instruction_passport_global@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/instruction_passport_global@2x.png'
      }
    }
  },
  'general-book': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_general_book_first.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_general_book_first@2x.png',
        i18nKeyIntroductionImage3: '/img/introduction/identity-document/sp_manual_general_book_first@3x.png'
      },
      diagonal: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_general_book_diagonal.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_general_book_diagonal@2x.png',
        i18nKeyIntroductionImage3: '/img/introduction/identity-document/sp_manual_general_book_diagonal@3x.png'
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_general_book_second.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_general_book_second@2x.png',
        i18nKeyIntroductionImage3: '/img/introduction/identity-document/sp_manual_general_book_second@3x.png'
      }
    }
  },
  'general-card': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_general_card_front.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_general_card_front@2x.png'
      },
      diagonal: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_general_card_diagonal.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_general_card_diagonal@2x.png'
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_general_card_back.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_general_card_back@2x.png'
      }
    }
  },
  'health-insurance-card': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_health_insurance_card_front_ja@1x.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_manual_health_insurance_card_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_manual_health_insurance_card_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_manual_health_insurance_card_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_manual_health_insurance_card_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_manual_health_insurance_card_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_manual_health_insurance_card_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_manual_health_insurance_card_diagonal_zh-cn.mp4'
        }
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_health_insurance_card_back_ja@1x.png'
      }
    }
  },
  'my-number-card': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_my_number_card_front_ja.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_manual_my_number_card_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_manual_my_number_card_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_manual_my_number_card_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_manual_my_number_card_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_manual_my_number_card_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_manual_my_number_card_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_manual_my_number_card_diagonal_zh-cn.mp4'
        }
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_my_number_card_back_ja.png'
      }
    }
  },
  'my-number-card-auto': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_autodetect_my_number_card_front_ja.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_autodetect_my_number_card_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_autodetect_my_number_card_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_autodetect_my_number_card_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_autodetect_my_number_card_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_autodetect_my_number_card_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_autodetect_my_number_card_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_autodetect_my_number_card_diagonal_zh-cn.mp4'
        }
      }
    }
  },
  passport: {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_passport_front_ja.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_manual_passport_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_manual_passport_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_manual_passport_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_manual_passport_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_manual_passport_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_manual_passport_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_manual_passport_diagonal_zh-cn.mp4'
        }
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_passport_back_ja.png'
      }
    }
  },
  'residence-card': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_residence_card_front_ja.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_manual_residence_card_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_manual_residence_card_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_manual_residence_card_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_manual_residence_card_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_manual_residence_card_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_manual_residence_card_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_manual_residence_card_diagonal_zh-cn.mp4'
        }
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_residence_card_back_ja.png'
      }
    }
  },
  'special-permanent-resident-certificate': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1:
          '/img/introduction/identity-document/sp_manual_special_permanent_resident_certificate_front_ja.png',
        i18nKeyIntroductionImage2:
          '/img/introduction/identity-document/sp_manual_special_permanent_resident_certificate_front_ja@2x.png',
        i18nKeyIntroductionImage3:
          '/img/introduction/identity-document/sp_manual_special_permanent_resident_certificate_front_ja@3x.png'
      },
      diagonal: {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/identity-document/sp_manual_special_permanent_resident_certificate_diagonal_ja.mp4',
          vi: '/mov/introduction/identity-document/sp_manual_special_permanent_resident_certificate_diagonal_vi.mp4',
          en: '/mov/introduction/identity-document/sp_manual_special_permanent_resident_certificate_diagonal_en.mp4',
          id: '/mov/introduction/identity-document/sp_manual_special_permanent_resident_certificate_diagonal_id.mp4',
          ms: '/mov/introduction/identity-document/sp_manual_special_permanent_resident_certificate_diagonal_ms.mp4',
          th: '/mov/introduction/identity-document/sp_manual_special_permanent_resident_certificate_diagonal_th.mp4',
          zh: '/mov/introduction/identity-document/sp_manual_special_permanent_resident_certificate_diagonal_zh-cn.mp4'
        }
      },
      back: {
        i18nKeyIntroductionImage1:
          '/img/introduction/identity-document/sp_manual_special_permanent_resident_certificate_back_ja.png',
        i18nKeyIntroductionImage2:
          '/img/introduction/identity-document/sp_manual_special_permanent_resident_certificate_back_ja@2x.png',
        i18nKeyIntroductionImage3:
          '/img/introduction/identity-document/sp_manual_special_permanent_resident_certificate_back_ja@3x.png'
      }
    }
  },
  'mynumber-document-notification-card': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_notification_card_front_ja.png'
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_notification_card_back_ja.png'
      }
    }
  },
  'mynumber-document-residence-certificate': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_residence_certificate_front_ja.png'
      }
    }
  },
  'mother-and-baby-notebook': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/sp_manual_mother_and_baby_notebook.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/sp_manual_mother_and_baby_notebook@2x.png',
        i18nKeyIntroductionImage3: '/img/introduction/identity-document/sp_manual_mother_and_baby_notebook@3x.png'
      }
    }
  },
  'certificate-of-residence': {
    pc: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_residence_certificate_front_ja.png'
      }
    },
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_residence_certificate_front_ja.png'
      }
    }
  },
  'certificate-of-family-register': {
    pc: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_residence_certificate_front_ja.png'
      }
    },
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_residence_certificate_front_ja.png'
      }
    }
  },
  'certificate-of-individual-records': {
    pc: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_residence_certificate_front_ja.png'
      }
    },
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/mynumber-document/sp_manual_residence_certificate_front_ja.png'
      }
    }
  },
  'mynumber-document-unknown': {
    pc: {},
    sp: {}
  },
  'optioal-document': {
    pc: {},
    sp: {}
  },
  'general-purpose-document': {
    pc: {},
    sp: {}
  },
  face: {
    pc: {
      'face-front': {
        i18nKeyIntroductionImage1: '/img/introduction/face/pc_manual_face_front_ja@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/face/pc_manual_face_front_ja@2x.png',
        i18nKeyIntroductionImage3: '/img/introduction/face/pc_manual_face_front_ja@3x.png'
      },
      'face-action': {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/face/pc_autodetect_face_action_ja.mp4',
          vi: '/mov/introduction/face/pc_autodetect_face_action_vi.mp4',
          en: '/mov/introduction/face/pc_autodetect_face_action_en.mp4',
          id: '/mov/introduction/face/pc_autodetect_face_action_id.mp4',
          ms: '/mov/introduction/face/pc_autodetect_face_action_ms.mp4',
          th: '/mov/introduction/face/pc_autodetect_face_action_th.mp4',
          zh: '/mov/introduction/face/pc_autodetect_face_action_zh-cn.mp4'
        }
      }
    },
    sp: {
      'face-front': {
        i18nKeyIntroductionImage1: '/img/introduction/face/sp_manual_face_front_ja@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/face/sp_manual_face_front_ja@2x.png'
      },
      'face-action': {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/face/sp_autodetect_face_action_ja.mp4',
          vi: '/mov/introduction/face/sp_autodetect_face_action_vi.mp4',
          en: '/mov/introduction/face/sp_autodetect_face_action_en.mp4',
          id: '/mov/introduction/face/sp_autodetect_face_action_id.mp4',
          ms: '/mov/introduction/face/sp_autodetect_face_action_ms.mp4',
          th: '/mov/introduction/face/sp_autodetect_face_action_th.mp4',
          zh: '/mov/introduction/face/sp_autodetect_face_action_zh-cn.mp4'
        }
      }
    }
  }
};

/**
 * 書類ごとのインストラクション画像・動画（Global）
 * PCは基本的にコンポーネント側で共通になっている
 */
export const instructionMediaByGlobalDocument: {
  [key in
    | I18nDocumentName
    | 'general-book'
    | 'general-card'
    | 'general-purpose-document'
    | 'optioal-document'
    | 'face'
    | 'mynumber-document-notification-card'
    | 'mynumber-document-residence-certificate'
    | 'mynumber-document-unknown']?: {
    [key in DeviceType]: {
      [key in ShootType]?: MediaList;
    };
  };
} = {
  'general-card': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/instruction_general_card_front_global@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/instruction_general_card_front_global@2x.png'
      },
      back: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/instruction_general_card_back_global@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/instruction_general_card_back_global@2x.png'
      }
    }
  },
  'global-passport': {
    pc: {},
    sp: {
      front: {
        i18nKeyIntroductionImage1: '/img/introduction/identity-document/instruction_passport_global@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/identity-document/instruction_passport_global@2x.png'
      }
    }
  },
  'optioal-document': {
    pc: {},
    sp: {}
  },
  'general-purpose-document': {
    pc: {},
    sp: {}
  },
  face: {
    pc: {
      'face-front': {
        i18nKeyIntroductionImage1: '/img/introduction/face/pc_manual_face_front_ja@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/face/pc_manual_face_front_ja@2x.png',
        i18nKeyIntroductionImage3: '/img/introduction/face/pc_manual_face_front_ja@3x.png'
      },
      'face-action': {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/face/pc_autodetect_face_action_ja.mp4',
          vi: '/mov/introduction/face/pc_autodetect_face_action_vi.mp4',
          en: '/mov/introduction/face/pc_autodetect_face_action_en.mp4',
          id: '/mov/introduction/face/pc_autodetect_face_action_id.mp4',
          ms: '/mov/introduction/face/pc_autodetect_face_action_ms.mp4',
          th: '/mov/introduction/face/pc_autodetect_face_action_th.mp4',
          zh: '/mov/introduction/face/pc_autodetect_face_action_zh-cn.mp4'
        }
      }
    },
    sp: {
      'face-front': {
        i18nKeyIntroductionImage1: '/img/introduction/face/sp_manual_face_front_ja@1x.png',
        i18nKeyIntroductionImage2: '/img/introduction/face/sp_manual_face_front_ja@2x.png'
      },
      'face-action': {
        i18nKeyIntroductionMovie: {
          ja: '/mov/introduction/face/sp_autodetect_face_action_ja.mp4',
          vi: '/mov/introduction/face/sp_autodetect_face_action_vi.mp4',
          en: '/mov/introduction/face/sp_autodetect_face_action_en.mp4',
          id: '/mov/introduction/face/sp_autodetect_face_action_id.mp4',
          ms: '/mov/introduction/face/sp_autodetect_face_action_ms.mp4',
          th: '/mov/introduction/face/sp_autodetect_face_action_th.mp4',
          zh: '/mov/introduction/face/sp_autodetect_face_action_zh-cn.mp4'
        }
      }
    }
  }
};
